import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Container,
} from '@mui/material';
import { Email as EmailIcon, Home as HomeIcon, Phone as PhoneIcon } from '@mui/icons-material';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import bg1 from '../../assets/bg1.png';
import config from '../../Config';
import swal from 'sweetalert';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    organisation: '',
    message: '',
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const validate = () => {
    let temp = {};
    let isValid = true;

    if (!formData.name.trim()) {
      temp.name = 'Name is required';
      isValid = false;
    }

    if (!formData.email.trim()) {
      temp.email = 'Email is required';
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        temp.email = 'Enter a valid email';
        isValid = false;
      }
    }

    if (!formData.message.trim()) {
      temp.message = 'Message is required';
      isValid = false;
    }

    setErrors(temp);
    return isValid;
  };

  const handleNumberChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setFormData({ ...formData, number: value });
      setErrors((prev) => ({ ...prev, number: '' }));
    } else {
      setErrors((prev) => ({ ...prev, number: 'Phone number must be numeric' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      swal('Error', 'Please fill all required fields.', 'error');
      return;
    }

    try {
      const response = await fetch(`${config.apiBaseUrl}/admin/create-leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        swal('Success', 'Message sent successfully!', 'success');
        setFormData({
          name: '',
          email: '',
          number: '',
          organisation: '',
          message: '',
        });
      } else {
        swal('Error', 'Failed to send message.', 'error');
      }
    } catch (error) {
      swal('Error', 'Something went wrong. Please try again.', 'error');
    }
  };

  const isFormValid =
    formData.name.trim() &&
    formData.email.trim() &&
    formData.message.trim() &&
    !errors.name &&
    !errors.email &&
    !errors.number;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: `url(${bg1})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        flexDirection: 'column',
        color: '#fff',
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          zIndex: -1,
        }}
      />

      <Navbar />

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          py: 4,
          px: { xs: 2, md: 0 },
        }}
      >
        <Container maxWidth="lg" sx={{ zIndex: 1 }}>
          <Grid
            container
            spacing={4}
            alignItems="stretch"
            sx={{
              flexWrap: { xs: 'wrap', md: 'nowrap' },
            }}
          >
            {/* Left Card */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: { xs: '100%', md: '50%' },
                height: '100%',
                boxSizing: 'border-box',
                maxWidth: '100%',
              }}
            >
              <Card
                sx={{
                  flexGrow: 1,
                  borderRadius: 3,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  boxSizing: 'border-box',
                  overflowWrap: 'break-word',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  color: '#333',
                }}
              >
                <CardContent
                  sx={{
                    p: 4,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="h3"
                    component="h1"
                    sx={{
                      fontWeight: 'bold',
                      mb: 3,
                      color: '#333',
                      wordBreak: 'break-word',
                    }}
                  >
                    Contact Us
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#666',
                      mb: 4,
                      lineHeight: 1.6,
                    }}
                  >
                    We are committed to processing the information in order to contact you and talk
                    about your project.
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <EmailIcon sx={{ color: '#333', fontSize: 24 }} />
                      <Typography variant="body1" sx={{ color: '#333' }}>
                        info@homeslygroup.com
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <HomeIcon sx={{ color: '#333', fontSize: 24, mt: 0.5 }} />
                      <Box>
                        <Typography variant="body1" sx={{ color: '#333' }}>
                          124-128 City Road, London
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#333' }}>
                          England EC1V 2NX
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <PhoneIcon sx={{ color: '#333', fontSize: 24 }} />
                      <Typography variant="body1" sx={{ color: '#333' }}>
                        02045259955
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Form */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: { xs: '100%', md: '50%' },
                height: '100%',
                boxSizing: 'border-box',
                maxWidth: '100%',
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  p: { xs: 2, md: 4 },
                  width: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2.5,
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    maxWidth: '100%',
                  }}
                >
                  <TextField
                    name="name"
                    placeholder="Name *"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    error={!!errors.name}
                    sx={inputStyle}
                  />
                  {errors.name && (
                    <Typography sx={{ color: 'red', fontSize: '0.85rem', mt: -1 }}>
                      {errors.name}
                    </Typography>
                  )}

                  <TextField
                    name="email"
                    placeholder="Email *"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    error={!!errors.email}
                    sx={inputStyle}
                  />
                  {errors.email && (
                    <Typography sx={{ color: 'red', fontSize: '0.85rem', mt: -1 }}>
                      {errors.email}
                    </Typography>
                  )}

                  <TextField
                    name="number"
                    placeholder="Phone Number"
                    value={formData.number}
                    onChange={handleNumberChange}
                    fullWidth
                    variant="outlined"
                    error={!!errors.number}
                    sx={inputStyle}
                  />
                  {errors.number && (
                    <Typography sx={{ color: 'red', fontSize: '0.85rem', mt: -1 }}>
                      {errors.number}
                    </Typography>
                  )}

                  <TextField
                    name="organisation"
                    placeholder="Organisation"
                    value={formData.organisation}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    sx={inputStyle}
                  />

                  <TextField
                    name="message"
                    placeholder="Message *"
                    value={formData.message}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    sx={{ ...inputStyle, mb: 1 }}
                  />
                  {errors.message && (
                    <Typography sx={{ color: 'red', fontSize: '0.85rem', mt: -1 }}>
                      {errors.message}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={!isFormValid}
                    sx={{
                      background: 'linear-gradient(135deg, #8B5CF6 0%, #F59E0B 100%)',
                      color: '#FFFFFF',
                      py: 2,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                      opacity: !isFormValid ? 0.6 : 1,
                      pointerEvents: !isFormValid ? 'none' : 'auto',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #7C3AED 0%, #D97706 100%)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    Submit
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
}

const inputStyle = {
  maxWidth: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    borderRadius: 2,
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    py: 2,
  },
};
